import React, { useEffect, useState } from 'react';
import { Download, X } from 'lucide-react';

/**
 * PWA "Ana Ekrana Ekle" floating action button.
 * - Listens for the beforeinstallprompt event (Chrome / Edge / Samsung)
 * - Hides itself once the app is installed (display-mode: standalone) or user dismissed
 * - On iOS Safari (no beforeinstallprompt support) — falls back to a brief help tooltip
 */
const PWAInstallButton = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIosHelp, setShowIosHelp] = useState(false);

  useEffect(() => {
    // Already installed → never show
    if (window.matchMedia('(display-mode: standalone)').matches) return;
    if (window.navigator.standalone) return;
    if (localStorage.getItem('pwaDismissed') === 'true') return;

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', () => setVisible(false));

    // iOS Safari fallback — manual "Add to Home Screen" hint
    const ua = window.navigator.userAgent || '';
    const isIos = /iPad|iPhone|iPod/.test(ua);
    if (isIos && !window.navigator.standalone) {
      setTimeout(() => setVisible(true), 4000);
      setShowIosHelp(true);
    }
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const install = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      try { await deferredPrompt.userChoice; } catch { /* ignored */ }
      setDeferredPrompt(null);
      setVisible(false);
    }
  };

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem('pwaDismissed', 'true');
  };

  if (!visible) return null;

  return (
    <div
      data-testid="pwa-install-card"
      className="fixed bottom-20 left-3 right-3 sm:left-auto sm:right-4 sm:bottom-20 sm:max-w-sm z-[9990] bg-charcoal text-white rounded-2xl shadow-2xl ring-1 ring-white/10 p-3 flex items-center gap-3 animate-in slide-in-from-bottom-4 duration-500"
    >
      <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-terracotta to-orange-700 flex items-center justify-center flex-shrink-0 shadow-lg shadow-terracotta/30">
        <Download className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm">Uygulamayı Yükle</p>
        <p className="text-[11px] text-white/70 leading-tight">
          {showIosHelp
            ? 'Paylaş > Ana Ekrana Ekle ile telefonunuza ekleyin'
            : 'Hızlı erişim için ana ekranınıza ekleyin'}
        </p>
      </div>
      {!showIosHelp && (
        <button
          onClick={install}
          data-testid="pwa-install-btn"
          className="px-3 py-1.5 bg-terracotta text-white text-xs font-semibold rounded-full hover:bg-terracotta/90 active:scale-95 transition-all flex-shrink-0"
        >
          Ekle
        </button>
      )}
      <button
        onClick={dismiss}
        data-testid="pwa-dismiss-btn"
        className="p-1 text-white/50 hover:text-white flex-shrink-0"
        aria-label="Kapat"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default PWAInstallButton;
